import React, { useState, useMemo, useEffect, useRef } from 'react';
import { LENS_GROUPS, LENSES, LensId } from '../data/lenses';
import type { Parcel } from '../data/useParcels';
import { T } from '../theme/tokens';

interface Item { key: string; label: string; sub: string; kind: 'lot' | 'lens'; run: () => void; }

/** ⌘K command palette — jump to any lot or switch the active lens by typing. */
export default function CommandPalette({ open, onClose, parcels, onSelectLot, onSetLens }:
  { open: boolean; onClose: () => void; parcels: Parcel[]; onSelectLot: (id: string) => void; onSetLens: (id: LensId) => void }) {
  const [q, setQ] = useState('');
  const [i, setI] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const invokerRef = useRef<HTMLElement | null>(null);

  // Capture the invoking element on open and RETURN focus to it on close — standard dialog
  // behavior; without this, closing the palette dropped keyboard users at <body>.
  useEffect(() => {
    if (open) {
      invokerRef.current = document.activeElement as HTMLElement | null;
      setQ(''); setI(0); setTimeout(() => inputRef.current?.focus(), 0);
    } else {
      invokerRef.current?.focus?.(); invokerRef.current = null;
    }
  }, [open]);

  const items = useMemo<Item[]>(() => {
    const s = q.trim().toLowerCase();
    const lenses = LENS_GROUPS.flatMap(g => g.ids).map(id => ({
      key: 'lens:' + id, kind: 'lens' as const, label: 'Color by ' + LENSES[id].label, sub: 'Lens',
      run: () => { onSetLens(id); onClose(); },
    })).filter(c => !s || c.label.toLowerCase().includes(s));
    const lots = !s ? [] : parcels.filter(p =>
      (p.name || '').toLowerCase().includes(s) || (p.model || '').toLowerCase().includes(s) ||
      (p.unit?.buyer || '').toLowerCase().includes(s) || (p.permitNumber || '').toLowerCase().includes(s) ||
      (p.blockLot || '').toLowerCase().includes(s) ||
      (p.address || '').toLowerCase().includes(s) || (p.newParcelNumber || '').toLowerCase().includes(s)
    ).slice(0, 8).map(p => {
      // Show the street address in the label so an address-number search visibly matches
      // ("1949" → "396 · 1949 Rashi Dr."); buyer/model/phase stay in the subtitle.
      const addrShort = p.address ? p.address.split(',')[0].trim() : '';
      return {
        key: 'lot:' + p.id, kind: 'lot' as const, label: addrShort ? `${p.name} · ${addrShort}` : p.name,
        sub: [p.unit?.buyer || p.buyersAll, p.model, p.salesBucket !== 'N/A' ? p.salesBucket : '', p.geo.phase].filter(Boolean).join(' · '),
        run: () => { onSelectLot(p.id); onClose(); },
      };
    });
    return [...lots, ...(s ? lenses : lenses.slice(0, 6))];
  }, [q, parcels, onSelectLot, onSetLens, onClose]);

  useEffect(() => { setI(0); }, [q]);

  if (!open) return null;
  const onKey = (e: React.KeyboardEvent) => {
    // stopPropagation: Esc closes ONLY the palette — without it the app-level window handler on
    // the same keypress also deselected the lot / closed the detail panel behind the dialog.
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setI(x => Math.min(items.length - 1, x + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setI(x => Math.max(0, x - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); items[i]?.run(); }
  };

  return (
    <div onMouseDown={onClose} style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(28,38,32,.28)', display: 'grid', placeItems: 'start center', paddingTop: '12vh', backdropFilter: 'blur(2px)' }}>
      <div onMouseDown={e => e.stopPropagation()} onKeyDown={onKey}
        role="dialog" aria-modal="true" aria-label="Command palette — jump to a lot or switch lens"
        onKeyDownCapture={e => {  // minimal focus trap: Tab cycles within the dialog
          if (e.key !== 'Tab') return;
          const root = e.currentTarget as HTMLElement;
          const els = root.querySelectorAll<HTMLElement>('input, button');
          if (!els.length) return;
          const first = els[0], last = els[els.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }}
        style={{ width: 'min(560px, 92vw)', background: T.paper, border: `1px solid ${T.line}`, borderRadius: 14, boxShadow: '0 24px 70px rgba(28,38,32,.35)', overflow: 'hidden' }}>
        <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
          placeholder="Jump to a lot, address, buyer, permit… or “Color by …”"
          style={{ width: '100%', padding: '15px 18px', border: 'none', borderBottom: `1px solid ${T.line}`, fontSize: 15, background: T.surface, outline: 'none' }} />
        <div className="lfe-scroll" style={{ maxHeight: '46vh', overflow: 'auto', padding: 6 }}>
          {items.length === 0 && <div style={{ padding: '14px 16px', color: T.inkSoft, fontSize: 13 }}>No matches.</div>}
          {items.map((it, idx) => (
            <button key={it.key} onMouseEnter={() => setI(idx)} onClick={it.run} className="lfe-press"
              style={{ display: 'flex', alignItems: 'baseline', gap: 10, width: '100%', textAlign: 'left', padding: '9px 12px', borderRadius: 9, border: 'none', cursor: 'pointer', background: idx === i ? T.sand2 : 'transparent' }}>
              <span style={{ fontSize: 10, color: it.kind === 'lens' ? T.accent : T.gold, fontWeight: 700, width: 30, flex: '0 0 auto' }}>{it.kind === 'lens' ? 'LENS' : 'LOT'}</span>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{it.label}</span>
              <span className="num" title={it.sub || undefined} style={{ marginLeft: 'auto', fontSize: 11, color: T.inkSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>{it.sub}</span>
            </button>
          ))}
        </div>
        <div style={{ padding: '7px 14px', borderTop: `1px solid ${T.line}`, fontSize: 11, color: T.inkSoft, display: 'flex', gap: 14 }}>
          <span>↑↓ navigate</span><span>↵ open</span><span>esc close</span>
        </div>
      </div>
    </div>
  );
}
